import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import Loader from 'react-loader-spinner';
import { FiFileText, FiSave } from 'react-icons/fi';
import { API_URLS } from '../constants/config';
import { useI18n } from '../utilities/i18n';
import { authorizedPost, getApiErrorMessage } from '../utilities/stadiumsApi';
import { useChampionships } from '../utilities/championships';
import { useAdminAccess } from '../utilities/adminAccess';
import { extractEntity, extractList } from '../utilities/tournamentsApi';
import { extractPlainTextFromWordFile } from '../utilities/docxText';
import StadiumCrumbs from '../components/StadiumCrumbs';
import '../styles/createMatchModal.css';
import '../styles/stadiumsManagement.css';
import '../styles/tournamentsManagement.css';

export default function ManageLeagueRules() {
  const { countryId, leagueId } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useI18n();
  const { champId, loading: champsLoading } = useChampionships();
  const { canWrite } = useAdminAccess('tournaments');

  const [leagueName, setLeagueName] = useState('');
  const [countryName, setCountryName] = useState('');
  const [rules, setRules] = useState('');
  const [rulesOriginal, setRulesOriginal] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const wordInputRef = useRef(null);
  const rulesDirty = rules !== rulesOriginal;

  useEffect(() => {
    if (!champId && champsLoading) return;
    initPage();
  }, [countryId, leagueId, champId, champsLoading]);

  const initPage = async () => {
    setMessage({ text: '', type: '' });
    setRules('');
    setRulesOriginal('');

    if (!champId) {
      setLoading(false);
      setMessage({ text: t('championshipsLoadError'), type: 'error' });
      return;
    }

    if (!leagueId) {
      setLoading(false);
      setMessage({ text: t('stLoadError'), type: 'error' });
      return;
    }

    setLoading(true);
    try {
      const [countriesResponse, leagueResponse, rulesResponse] = await Promise.all([
        authorizedPost(dispatch, API_URLS.getAdminCountries, { champ_id: champId }),
        authorizedPost(dispatch, API_URLS.getAdminLeague, { id: Number(leagueId) }),
        authorizedPost(dispatch, API_URLS.getAdminLeagueRules, { id: Number(leagueId) }),
      ]);

      const countries = extractList(countriesResponse.data);
      const country = countries.find((item) => Number(item.id) === Number(countryId));
      setCountryName(country?.name || '');

      const league = extractEntity(leagueResponse.data);
      setLeagueName(league.name || '');
      if (league.country_id) {
        const leagueCountry = countries.find((item) => Number(item.id) === Number(league.country_id));
        if (leagueCountry) setCountryName(leagueCountry.name);
      }

      const rulesPayload = extractEntity(rulesResponse.data);
      const nextRules = typeof rulesPayload.rules === 'string' ? rulesPayload.rules : '';
      setRules(nextRules);
      setRulesOriginal(nextRules);
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('stLoadError')), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ text: '', type: '' });
    try {
      const response = await authorizedPost(dispatch, API_URLS.changeAdminLeagueRules, {
        id: Number(leagueId),
        rules: String(rules ?? ''),
      });
      const saved = extractEntity(response.data);
      const nextRules = typeof saved.rules === 'string' ? saved.rules : String(rules ?? '');
      setRules(nextRules);
      setRulesOriginal(nextRules);
      setMessage({ text: t('stSaved'), type: 'success' });
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('stSaveError')), type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleImportWord = async (event) => {
    const file = event?.target?.files?.[0];
    if (event?.target) event.target.value = '';
    if (!file || !canWrite) return;

    setImporting(true);
    setMessage({ text: '', type: '' });
    try {
      const text = await extractPlainTextFromWordFile(file);
      if (!String(text || '').trim()) {
        setMessage({ text: t('trRulesWordEmpty'), type: 'error' });
        return;
      }
      setRules(String(text));
      setMessage({ text: t('trRulesWordImported'), type: 'success' });
    } catch (error) {
      if (error?.code === 'LEGACY_DOC') {
        setMessage({ text: t('trRulesWordLegacy'), type: 'error' });
      } else if (error?.code === 'NOT_DOCX') {
        setMessage({ text: t('trRulesWordType'), type: 'error' });
      } else {
        setMessage({ text: t('trRulesWordError'), type: 'error' });
      }
    } finally {
      setImporting(false);
    }
  };

  const crumbs = [
    { label: t('navTournaments'), to: '/tournaments' },
    { label: countryName || t('trCountry'), to: `/tournaments/${countryId}` },
    { label: leagueName || t('trEditLeague'), to: `/tournaments/${countryId}/league/${leagueId}` },
    { label: t('trRules') },
  ];

  return (
    <div className="layoutWrapper st-layout">
      <div className="matchCenterWrapper st-page tr-rules-page">
        <div className="matchCenterHeader">
          <div className="st-header">
            <StadiumCrumbs items={crumbs} />
          </div>
        </div>

        <div className="st-scroll">
          <div className="st-content">
            {message.text ? <div className={`st-message ${message.type}`}>{message.text}</div> : null}

            {loading ? (
              <div className="st-loading">
                <Loader type="Oval" color="#3ad1a0" height={48} width={48} />
                <p>{t('trLoading')}</p>
              </div>
            ) : (
              <div className="st-block tr-rules-form">
                <h2 className="st-block-header">{t('trRules')}</h2>
                <p className="st-hint">{t('trRulesHint')}</p>

                {canWrite ? (
                  <div className="tr-rules-import">
                    <input
                      ref={wordInputRef}
                      type="file"
                      accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      className="st-photo-input"
                      disabled={importing || saving}
                      onChange={handleImportWord}
                    />
                    <button
                      type="button"
                      className="st-btn st-btn-secondary"
                      disabled={importing || saving}
                      onClick={() => wordInputRef.current?.click()}
                    >
                      <FiFileText />
                      {importing ? t('trLoading') : t('trRulesWordImport')}
                    </button>
                    <span className="st-hint" style={{ margin: 0 }}>{t('trRulesWordHint')}</span>
                  </div>
                ) : null}

                <div className="st-field">
                  <textarea
                    className="st-textarea tr-rules-textarea"
                    rows={20}
                    value={rules}
                    placeholder={t('trRulesPh')}
                    disabled={!canWrite || saving || importing}
                    onChange={(event) => setRules(event.target.value)}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="st-actions st-actions-split">
          <button
            type="button"
            className="st-btn st-btn-secondary"
            disabled={saving || importing}
            onClick={() => history.push(`/tournaments/${countryId}/league/${leagueId}`)}
          >
            {t('stBack')}
          </button>
          {canWrite ? (
            <button
              type="button"
              className="st-btn st-btn-primary"
              disabled={loading || saving || importing || !rulesDirty}
              onClick={handleSave}
            >
              <FiSave />
              {saving ? t('trLoading') : t('trRulesSave')}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
